import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faqAPI } from '@/services/api';
import { Plus, Trash2, Pencil, X, Save, Eye, EyeOff, Sparkles, Globe, ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const LANG_LABELS = { en: 'English', pt: 'Portugues', es: 'Espanol', fr: 'Francais', de: 'Deutsch', pl: 'Polski' };
const ALL_LANGS = ['en', 'pt', 'es', 'fr', 'de', 'pl'];

const emptyTrans = () => ({ question: '', answer: '' });

function emptyForm() {
	return {
		id: null,
		category: '',
		order: 100,
		is_published: true,
		translations: { en: emptyTrans() },
	};
}

export default function FAQAdminManager() {
	const { t } = useTranslation();
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState(null); // form state when dialog open
	const [activeLang, setActiveLang] = useState('en');
	const [feedbackAggregates, setFeedbackAggregates] = useState({});
	const [feedbackComments, setFeedbackComments] = useState([]);
	const [showComments, setShowComments] = useState(false);

	const reload = async () => {
		setLoading(true);
		try {
			const [r, fb] = await Promise.all([
				faqAPI.listAdmin(),
				faqAPI.adminFeedback({ only_with_comments: false }).catch(() => ({ data: { items: [], aggregates: {} } })),
			]);
			setItems(r.data?.items || []);
			setFeedbackAggregates(fb.data?.aggregates || {});
			setFeedbackComments((fb.data?.items || []).filter((it) => it.comment));
		} catch { toast.error(t('common.error')); }
		setLoading(false);
	};

	useEffect(() => { reload(); }, []);

	const openNew = () => { setActiveLang('en'); setEditing(emptyForm()); };
	const openEdit = (faq) => {
		setActiveLang('en');
		setEditing({
			id: faq.id,
			category: faq.category || '',
			order: faq.order ?? 100,
			is_published: !!faq.is_published,
			translations: faq.translations || { en: emptyTrans() },
		});
	};

	const setTrans = (lang, field, value) => {
		setEditing((prev) => ({
			...prev,
			translations: { ...prev.translations, [lang]: { ...(prev.translations[lang] || emptyTrans()), [field]: value } },
		}));
	};

	const removeTrans = (lang) => {
		if (lang === 'en') return;
		setEditing((prev) => {
			const next = { ...prev.translations };
			delete next[lang];
			return { ...prev, translations: next };
		});
		if (activeLang === lang) setActiveLang('en');
	};

	const addTrans = (lang) => {
		setEditing((prev) => ({ ...prev, translations: { ...prev.translations, [lang]: emptyTrans() } }));
		setActiveLang(lang);
	};

	const save = async () => {
		if (!editing.category.trim()) { toast.error(t('common.error')); return; }
		if (!editing.translations.en?.question?.trim() || !editing.translations.en?.answer?.trim()) {
			toast.error(t('admin.faq_must_have_en'));
			return;
		}
		// strip empty non-en translations
		const cleaned = {};
		for (const [k, v] of Object.entries(editing.translations)) {
			if (k === 'en' || (v.question?.trim() && v.answer?.trim())) cleaned[k] = { question: v.question.trim(), answer: v.answer.trim() };
		}
		const payload = {
			category: editing.category.trim(),
			order: Number(editing.order) || 0,
			is_published: !!editing.is_published,
			translations: cleaned,
		};
		try {
			if (editing.id) await faqAPI.update(editing.id, payload);
			else await faqAPI.create(payload);
			toast.success(t('common.success'));
			setEditing(null);
			reload();
		} catch (e) {
			toast.error(e?.response?.data?.detail || t('common.error'));
		}
	};

	const remove = async (id) => {
		if (!window.confirm(t('admin.faq_delete_confirm'))) return;
		try { await faqAPI.remove(id); toast.success(t('common.success')); reload(); }
		catch { toast.error(t('common.error')); }
	};

	const togglePublish = async (faq) => {
		try {
			await faqAPI.update(faq.id, { is_published: !faq.is_published });
			reload();
		} catch { toast.error(t('common.error')); }
	};

	const seed = async () => {
		try { const r = await faqAPI.seed(); toast.success(`${t('admin.faq_seed_done')} (+${r.data.inserted})`); reload(); }
		catch { toast.error(t('common.error')); }
	};

	const presentLangs = editing ? Object.keys(editing.translations || {}) : [];
	const missingLangs = editing ? ALL_LANGS.filter((l) => !presentLangs.includes(l)) : [];

	return (
		<div className="space-y-4" data-testid="admin-faq-manager">
			<div className="flex items-center justify-between gap-3 flex-wrap">
				<div className="text-sm text-[#666055]">{items.length} FAQs</div>
				<div className="flex items-center gap-2">
					<Button size="sm" variant="outline" onClick={() => setShowComments((v) => !v)}
									className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-9 text-xs"
									data-testid="admin-faq-comments-btn">
						<MessageSquare className="w-3 h-3 mr-1" /> {t('admin.faq_feedback_comments')} ({feedbackComments.length})
					</Button>
					<Button size="sm" variant="outline" onClick={seed} className="border-[#E6E2D6] text-[#1A3C34]
							rounded-sm h-9 text-xs" data-testid="admin-faq-seed-btn">
						<Sparkles className="w-3 h-3 mr-1" /> {t('admin.faq_seed')}
					</Button>
					<Button size="sm" onClick={openNew} className="bg-[#1A3C34] hover:bg-[#153029] text-white
							rounded-sm h-9 text-xs" data-testid="admin-faq-new-btn">
						<Plus className="w-3 h-3 mr-1" /> {t('admin.faq_new')}
					</Button>
				</div>
			</div>

			{showComments && (
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-4" data-testid="admin-faq-comments-panel">
					<h4 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
						{t('admin.faq_feedback_comments')}
					</h4>
					{feedbackComments.length === 0 ? (
						<div className="text-xs text-[#666055]">{t('admin.faq_feedback_no_comments')}</div>
					) : (
						<ul className="divide-y divide-[#E6E2D6]">
							{feedbackComments.slice(0, 50).map((c) => {
								const linkedFaq = items.find((f) => f.id === c.faq_id);
								const en = linkedFaq?.translations?.en || {};
								return (
									<li key={c.id} className="py-3" data-testid={`admin-faq-comment-${c.id}`}>
										<div className="flex items-center gap-2 mb-1 flex-wrap">
											{c.helpful ? (
												<span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#1A3C34]
														bg-[#E6F0EC] px-2 py-0.5 rounded-sm">
                          <ThumbsUp className="w-3 h-3" /> {t('faq.helpful_yes')}
                        </span>
											) : (
												<span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#9B2C2C]
														bg-[#FBE7E7] px-2 py-0.5 rounded-sm">
                          <ThumbsDown className="w-3 h-3" /> {t('faq.helpful_no')}
                        </span>
											)}
											<span className="text-[11px] text-[#666055] truncate">{en.question || c.faq_id}</span>
											<span className="text-[10px] text-[#666055]/70 ml-auto">{(c.created_at || '').slice(0, 10)}</span>
										</div>
										<p className="text-sm text-[#3a3a3a] whitespace-pre-line">{c.comment}</p>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			)}

			{loading ? (
				<div className="text-sm text-[#666055]">Loading...</div>
			) : items.length === 0 ? (
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-12 text-center text-sm text-[#666055]"
						 data-testid="admin-faq-empty">
					{t('admin.faq_no_items')}
				</div>
			) : (
				<div className="space-y-2">
					{items.map((f) => {
						const langs = Object.keys(f.translations || {});
						const en = f.translations?.en || {};
						const agg = feedbackAggregates[f.id] || { helpful: 0, not_helpful: 0 };
						const catKey = f.category.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
						const catLabel = t(`faq.categories.${catKey}`, { defaultValue: f.category });
						return (
							<div key={f.id} className="bg-white border border-[#E6E2D6] rounded-sm p-4 flex items-start gap-3"
									 data-testid={`admin-faq-row-${f.id}`}>
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2 flex-wrap mb-1">
										<span className="bg-[#F2F0E9] text-[#666055] px-2 py-0.5 text-[10px] uppercase tracking-wider
												font-semibold rounded-sm">
											{ catLabel }
										</span>
										<span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold 
												rounded-sm ${f.is_published ? 'bg-[#1A3C34] text-white' : 'bg-[#E6E2D6] text-[#666055]'}`}>
                      {f.is_published ? t('admin.faq_published') : t('admin.faq_draft')}
                    </span>
										<span className="text-[10px] text-[#666055]">#{f.order}</span>
										<span className="text-[10px] text-[#666055] inline-flex items-center gap-1">
											<Globe className="w-3 h-3" /> {langs.join(', ')}
										</span>
										<span
											className="text-[10px] text-[#1A3C34] inline-flex items-center gap-1 ml-auto"
											title={t('admin.faq_feedback_helpful')}
											data-testid={`admin-faq-helpful-${f.id}`}	>
                      <ThumbsUp className="w-3 h-3" /> {agg.helpful}
                    </span>
										<span
											className="text-[10px] text-[#9B2C2C] inline-flex items-center gap-1"
											title={t('admin.faq_feedback_not_helpful')}
											data-testid={`admin-faq-not-helpful-${f.id}`} >
                      <ThumbsDown className="w-3 h-3" /> {agg.not_helpful}
                    </span>
									</div>
									<h4 className="text-sm font-semibold text-[#1A3C34] truncate font-['Playfair_Display']">{en.question}</h4>
									<p className="text-xs text-[#666055]/80 line-clamp-2 mt-1">{en.answer}</p>
								</div>
								<div className="flex items-center gap-1 flex-shrink-0">
									<Button size="sm" variant="outline" onClick={() => togglePublish(f)}
													className="border-[#E6E2D6] rounded-sm h-8 w-8 p-0"
													title={t('admin.faq_publish_toggle')} data-testid={`admin-faq-toggle-${f.id}`}>
										{f.is_published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
									</Button>
									<Button size="sm" variant="outline" onClick={() => openEdit(f)}
													className="border-[#E6E2D6] text-[#1A3C34] rounded-sm h-8 w-8 p-0"
													data-testid={`admin-faq-edit-${f.id}`}>
										<Pencil className="w-3 h-3" />
									</Button>
									<Button size="sm" variant="outline" onClick={() => remove(f.id)}
													className="border-[#E6E2D6] text-[#9B2C2C] rounded-sm h-8 w-8 p-0"
													data-testid={`admin-faq-delete-${f.id}`}>
										<Trash2 className="w-3 h-3" />
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
				<DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-faq-dialog">
					{editing && (
						<>
							<DialogHeader>
								<DialogTitle className="text-[#1A3C34] font-['Playfair_Display']">
									{editing.id ? 'Edit FAQ' : t('admin.faq_new')}
								</DialogTitle>
							</DialogHeader>
							<div className="space-y-4 pt-2">
								<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
									<div className="sm:col-span-2">
										<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-1">
											{t('admin.faq_category')}
										</label>
										<Input value={editing.category}
													 onChange={(e) => setEditing({ ...editing, category: e.target.value })}
													 maxLength={80} className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10"
													 data-testid="admin-faq-category-input" />
									</div>
									<div>
										<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-1">
											{t('admin.faq_order')}
										</label>
										<Input type="number" value={editing.order}
													 onChange={(e) => setEditing({ ...editing, order: e.target.value })}
													 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10"
													 data-testid="admin-faq-order-input" />
									</div>
								</div>

								<div className="flex items-center gap-3">
									<Switch checked={editing.is_published}
													onCheckedChange={(v) => setEditing({ ...editing, is_published: v })}
													data-testid="admin-faq-publish-switch" />
									<span className="text-sm text-[#1A3C34]">{t('admin.faq_publish_toggle')}</span>
								</div>

								<div>
									<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-2">
										{t('admin.faq_languages')}
									</label>
									<Tabs value={activeLang} onValueChange={setActiveLang}>
										<TabsList className="bg-[#F2F0E9] flex-wrap h-auto p-1">
											{presentLangs.map((l) => (
												<TabsTrigger
													key={l}
													value={l}
													className="data-[state=active]:bg-[#1A3C34] data-[state=active]:text-white text-xs px-3 py-1.5 rounded-sm"
													data-testid={`admin-faq-lang-tab-${l}`}
												>
													{LANG_LABELS[l] || l}
													{l !== 'en' && (
														<span
															className="ml-2 text-[#9B2C2C] hover:text-[#7B1F1F]"
															onClick={(e) => { e.stopPropagation(); removeTrans(l); }}
															data-testid={`admin-faq-lang-remove-${l}`}
														>
                              <X className="w-3 h-3 inline" />
                            </span>
													)}
												</TabsTrigger>
											))}
										</TabsList>
										{presentLangs.map((l) => (
											<TabsContent key={l} value={l} className="space-y-3 mt-3">
												<div>
													<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-1">
														{t('admin.faq_question')} ({LANG_LABELS[l] || l})
													</label>
													<Input
														value={editing.translations[l]?.question || ''}
														onChange={(e) => setTrans(l, 'question', e.target.value)}
														maxLength={300}
														className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-10"
														data-testid={`admin-faq-question-${l}`}
													/>
												</div>
												<div>
													<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-1">
														{t('admin.faq_answer')} ({LANG_LABELS[l] || l})
													</label>
													<Textarea
														value={editing.translations[l]?.answer || ''}
														onChange={(e) => setTrans(l, 'answer', e.target.value)}
														maxLength={8000}
														rows={6}
														className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm"
														data-testid={`admin-faq-answer-${l}`}
													/>
												</div>
											</TabsContent>
										))}
									</Tabs>

									{missingLangs.length > 0 && (
										<div className="mt-3 flex items-center gap-2 flex-wrap">
											<span className="text-[10px] text-[#666055] uppercase tracking-wider">
												+ {t('admin.faq_translation_for')}:
											</span>
											{missingLangs.map((l) => (
												<button
													key={l}
													type="button"
													onClick={() => addTrans(l)}
													className="text-xs px-2 py-1 border border-[#E6E2D6] rounded-sm text-[#1A3C34] hover:bg-[#F2F0E9]"
													data-testid={`admin-faq-lang-add-${l}`}
												>
													+ {LANG_LABELS[l] || l}
												</button>
											))}
										</div>
									)}
								</div>

								<div className="flex justify-end gap-2 pt-3 border-t border-[#E6E2D6]">
									<Button variant="ghost" onClick={() => setEditing(null)} className="text-[#666055]">
										<X className="w-4 h-4 mr-1" /> Cancel
									</Button>
									<Button onClick={save} className="bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm"
													data-testid="admin-faq-save-btn">
										<Save className="w-4 h-4 mr-1" /> {t('admin.faq_save')}
									</Button>
								</div>
							</div>
						</>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}